import { Component, Input, HostBinding, OnChanges, SimpleChanges, ChangeDetectorRef, EventEmitter, Output } from '@angular/core';
import { Contact, TelemetryStateResponse, AssetGeoLock, AssetSharedLocationTokenResponse, GeoLockProfileResponse, AssetLinkage } from '@key-telematics/fleet-api-client';
import { OverviewStateService, TelemetryItem } from '../../../services/state.service';
import { Router } from '@angular/router';
import { getIconStyles } from 'app/shared/components/cards';
import { SpatialUtils } from 'app/shared/components/map/map.utils';
import { UserTaskItem } from 'app/services';
import { IKuiDropdownMenuItem } from 'app/key-ui';
import { ModalService } from 'app/shared/components/modal';
import * as moment from 'moment-timezone';

export interface CustomFieldValue {
    id: string;
    name: string;
    value: string;
    type: string;
}

export interface AssetStateValue {
    id: string;
    name: string;
    value: string;
    color: string;
}

export interface LinkedAssetValue {
    id: string;
    name: string;
    type: string;
    icon: { name: string, color: string };
}

export interface IOValue {
    title: string;
    value: string;
    actions?: IKuiDropdownMenuItem[];
    tasks: UserTaskItem[];
}

@Component({
    selector: 'key-details-sidebar',
    templateUrl: 'details-sidebar.component.html',
    styleUrls: ['details-sidebar.component.scss'],
})
export class KeyDetailsSidebarComponent implements OnChanges {
    isOpen = true;

    @Input() ioValues: IOValue[];
    @Input() toggle: boolean;
    @Input() fields: CustomFieldValue[];
    @Input() contacts: Contact[];
    @Input() state: AssetStateValue[];
    @Input() telemetry: TelemetryStateResponse;
    @Input() linkedAssets: LinkedAssetValue[];
    @Input() linkedFrom: AssetLinkage;
    @Input() geoLock: AssetGeoLock;
    @Input() geoLockProfile: GeoLockProfileResponse;
    @Input() tasks: UserTaskItem[];
    @Input() shares: AssetSharedLocationTokenResponse[];

    @Output() userAction = new EventEmitter<string>();

    showAllTelemetry = false;
    showRawTelemetry = this.overviewState?.app?.user?.owner?.type === 'system';
    telemetryItems: TelemetryItem[] = [];
    geoLockViolated = false;
    geoLockEngaged = false;
    linkExpired = false;

    @HostBinding('class') hostClasses = 'border-width bg-card-block content-sidebar';
    @HostBinding('class.open') openClass = this.isOpen;

    constructor(
        private ref: ChangeDetectorRef,
        private overviewState: OverviewStateService,
        private router: Router,
        private modal: ModalService
    ) { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.telemetry) {
            this.buildTelemetry(this.showAllTelemetry);
        }

        this.geoLockEngaged = false;
        this.geoLockViolated = false;

        if (this.telemetry && this.telemetry.location && this.geoLock) {
            const distance = SpatialUtils.getDistanceFromLatLonInKm(this.telemetry.location.lat, this.telemetry.location.lon, this.geoLock.lat, this.geoLock.lon);
            this.geoLockViolated = (distance > this.geoLock.radiusKm);
        } else {
            if (this.geoLockProfile && this.telemetry?.telemetry['geo_lock'] !== undefined) {
                // we have a geolock profile assigned and the telemetry is showing that geolock is engaged
                this.geoLockEngaged = true;
                this.geoLockViolated = this.telemetry?.telemetry['geo_lock'] === 1;
            }
        }

        this.linkExpired = (this.linkedFrom && this.linkedFrom.end < moment.utc().toISOString());
    
    }

    buildTelemetry(showAll: boolean) {
        const sanitizedTelemetry = this.overviewState.getSanitizedTelemetry(this.telemetry);
        const telemetryItems: TelemetryItem[] = Object.values(sanitizedTelemetry);

        if (showAll && this.telemetry && this.telemetry.telemetry) {
            const { telemetry } = this.telemetry;
            Object.keys(telemetry).forEach(key => {
                telemetryItems.push({ key, name: key, value: telemetry[key] });
            });
        }

        this.telemetryItems = telemetryItems;
        this.showAllTelemetry = showAll;
        this.ref.markForCheck();
    }

    toggleSidebar() {
        this.openClass = !this.isOpen;

        if (this.isOpen) {
            this.isOpen = !this.isOpen;
        } else {
            setTimeout(() => this.isOpen = !this.isOpen, 300);
        }
    }

    getIconStyles(color: string): { color: string, background: string } {
        return getIconStyles(color);
    }

    navigateToAsset(id: string) {
        this.router.navigate(['/status', id, 'activity']);
    }

    openRawTelemetry() {
        this.modal.json(this.telemetry, 'Raw Telemetry');

    }
}
